/* eslint-disable max-lines */
/* eslint-disable react-func/max-lines-per-function */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog } from 'antd-mobile';
import { useLocation } from 'react-router';
import { App, AppInfo } from '@capacitor/app';
import lookie from 'lookie';
import { Icon } from '@fuxi/eevee-icon';
import { Device } from '@capacitor/device';
import { useDispatch } from 'react-redux';
import { setCurrentServingOrder } from '@/store/service';
import {
  ALL_PAGES,
  APP_LOCATION_IS_UNAVAILABLE,
  CURRENT_ORDER_IS_DRAFT,
  HAS_SHOWED_TIPS_MODAL,
  IS_OPEN_IMMER_MODE,
  SELECTED_CUSTOMER_ID,
  SELECTED_MACHINE_CAMERA_TYPE,
  UPPER_STATUS_CODE,
} from '@/constant';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { OneMoreOrderParams } from '@/pages/user/service-detail';
import { DisclaimerDialog, Header, HeaderMemberInfo, ScrollView } from '@/components';
import { Toast, getLocationCoord, handleNavFromOrderInfo, isNewSoftwareVersion } from '@/utils';
import { CustomToast, CustomToastRefAttrs } from '@/components/toast';
import useNav from '@/hooks/useNav';
import { useCheckLocationPermission, useDevice, useWeeklyPopup } from '@/hooks';
import service from '@/service';
import { useScanQRCode } from '@/hooks/useScanQRCode';
import { DisclaimerDialogRefAttrs } from '@/components/disclaimer-dialog';
import SelectPlan from './select-plan';
import { FilterDimension } from './select-plan/components/FilterHeader';

import cx from './index.module.scss';

interface Props {}

const AddService: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const { state } = useLocation() as {
    state: OneMoreOrderParams & { currentIsExpand: boolean; from: string } & {
      selectPlanItem: GetAllProductsResponse;
      selectMachineItem: DeviceItem;
    };
  };

  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [selectPlanItem, setSelectPlanItem] = useState<GetAllProductsResponse>({} as any);
  const [selectMachineItem, setSelectMachineItem] = useState<DeviceItem>({} as any);
  const [isCreatingService, setIsCreatingService] = useState(false);
  const customerId = useRef<number>(null);
  const customToastRef = useRef<CustomToastRefAttrs>(null);
  const deviceChassisState = useRef<Record<string, DeviceItem['chassisState']>>({});
  const deviceChassisLoopTimer = useRef<Record<string, NodeJS.Timeout>>({});
  const getAllPlanRef = useRef<VoidFunc>(null);
  const nav = useNav();
  const clauseDialogRef = useRef<DisclaimerDialogRefAttrs>(null);
  // 用于免责声明记录用户 所选的理疗部位和理疗类型
  const [selectedInfoByUser, setSelectedInfoByUser] = useState<FilterDimension>(null);
  const [storeRemainingInfo, setStoreRemainingInfo] = useState<StoreRemainingQuotaInfo>(null);
  const [appCoordInit, setAppCoordInit] = useState<{
    lat: number;
    lng: number;
    coordinateType: CreateServiceOrderV3Params['coordinateType'];
  }>({
    lat: null,
    lng: null,
    coordinateType: null,
  });

  const { shouldShowPopup } = useWeeklyPopup();
  const { scanQR } = useScanQRCode({
    tips: '扫描耗材二维码',
  });
  const { checkIsApp } = useDevice();
  const storeScanCodeRef = useRef<
    Record<
      string,
      {
        consumableUniqueCode: string;
        verifyCode: string;
      }
    >
  >({});

  const { checkLocationPermission } = useCheckLocationPermission();

  const scrollToBottom = useCallback(() => {
    try {
      scrollViewRef.current?.bsInstance?.scrollToElement('#machine-list', 300, 0, 0);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const submitBtnIsDisabeld = useMemo(
    () => !(selectPlanItem?.storeGoodsId || selectMachineItem?.deviceId),
    [selectMachineItem?.deviceId, selectPlanItem?.storeGoodsId]
  );

  // 当前方案是否在目前设备的消耗品管控范围
  const getShouldScanQRCodeList = useCallback(async (deviceId: string, selectPlanItem: GetAllProductsResponse) => {
    const shouldScanCodeList: Array<'consumableUniqueCode' | 'verifyCode'> = [];

    // 创建订单时获取最新的设备产品管控配置
    const { data: machineList } = await service.device.getDeviceList();

    const currentSelectDeviceInfo = machineList?.find(item => item.deviceId === deviceId);

    if (
      selectPlanItem?.items?.some((item: Massage.MassageItem) =>
        currentSelectDeviceInfo?.consumableItemTypes?.includes(item)
      )
    ) {
      shouldScanCodeList.push('consumableUniqueCode');
    }

    if (currentSelectDeviceInfo?.scanCode === 'on') {
      shouldScanCodeList.push('verifyCode');
    }

    return shouldScanCodeList;
  }, []);

  const getDeviceChassisState = useCallback(async (deviceId: string, showErrorMsg = false) => {
    if (!deviceId) return;

    const { data } = await service.device.getChassisStateByDeviceId(deviceId);

    switch (data?.state) {
      case 'Locking':
        deviceChassisState.current[deviceId] = 'Locking';
        showErrorMsg && Toast.fail('正在进行底盘停驻，不允许操作');
        break;
      case 'Unlocking':
        deviceChassisState.current[deviceId] = 'Unlocking';
        showErrorMsg && Toast.fail('正在进行底盘解锁，不允许操作');
        break;
      case 'Static':
        if (deviceChassisState.current[deviceId] === 'Locking') {
          Toast.show(`机器${deviceId}底盘停驻成功`);
        } else if (deviceChassisState.current[deviceId] === 'Unlocking') {
          Toast.show(`机器${deviceId}底盘解锁成功`);
        }
        clearInterval(deviceChassisLoopTimer.current[deviceId]);
        Reflect.deleteProperty(deviceChassisState.current, deviceId);
        break;
    }
  }, []);

  /** 当点击创建服务时底盘正在停驻或解锁时，轮询当前机器底盘状态并提示 */
  const loopGetDeviceChassisState = useCallback(
    async (deviceId: string) => {
      if (!deviceId) return;

      getDeviceChassisState(deviceId, true);

      // 当前正在轮询该设备的状态，则不再发起新的轮询
      if (deviceChassisLoopTimer.current[deviceId]) return;

      deviceChassisLoopTimer.current[deviceId] = setInterval(() => getDeviceChassisState(deviceId, false), 2000);
    },
    [getDeviceChassisState]
  );

  const handleCreateService = useCallback(async () => {
    try {
      await checkLocationPermission();
      console.log('定位权限校验通过');
    } catch (e) {
      console.log('定位权限校验失败', e);
      Toast.fail('请打开Pad的定位权限');
      return;
    }

    if (!selectPlanItem?.storeGoodsId) {
      customToastRef.current?.fail('请选择一个理疗方案');
      return;
    }
    if (!selectMachineItem?.deviceId) {
      customToastRef.current?.fail('请选择一台理疗设备');
      scrollToBottom();
      return;
    }
    try {
      setIsCreatingService(true);
      let appInfo: AppInfo = null;
      let appCoord: UnwrapPromise<ReturnType<typeof getLocationCoord>> = {
        lat: null,
        lng: null,
        coordinateType: null,
      };

      if (checkIsApp()) {
        appInfo = await App.getInfo();

        // 低于1.11.0版本使用高德SDK，判断应用是否因权限问题无法获取坐标信息
        if (!isNewSoftwareVersion(appInfo?.version, '1.11.0') && lookie.get(APP_LOCATION_IS_UNAVAILABLE)) {
          Toast.fail('该Pad应用权限配置有问题，无法获取位置信息，目前无法下单，请联系售后工程师处理', {
            duration: 5000,
          });
          return;
        }
        appCoord = appCoordInit;
      }

      /** 筋膜宝方案判断是否有外接设备 */
      if (
        selectPlanItem?.items?.[0] === 'HotStone' &&
        isNewSoftwareVersion(selectMachineItem?.softwareVersion, '1.8.10')
      ) {
        const state = await service.lanInterface.externalDevicePreflight({
          deviceId: selectMachineItem?.deviceId,
          item_type: 'HotStone',
        });
        if (!state?.installed) {
          Dialog.alert({
            title: '未检测到筋膜再生宝仪器',
            content: '请确认主机是否已接入机器人',
            confirmText: '我知道了',
            onConfirm() {},
          });
          return;
        }
      }

      // 判断当前设备是否在消耗品管控范围内
      let shouldScanQRCodeList = await getShouldScanQRCodeList(selectMachineItem?.deviceId, selectPlanItem);

      const storedCode = storeScanCodeRef.current?.[selectMachineItem?.deviceId];

      // 如果之前核销的时候，扫码过且有效，则该次不用扫码
      if (shouldScanQRCodeList?.length > 1 && !!storedCode) {
        !!storedCode?.consumableUniqueCode && (shouldScanQRCodeList = ['verifyCode']);
        !!storedCode?.verifyCode && (shouldScanQRCodeList = ['consumableUniqueCode']);
      }

      let qrRes: string[] = [];
      if (shouldScanQRCodeList?.length) {
        const deviceInfo = await Device.getInfo();
        if (deviceInfo.platform === 'web') {
          Toast.show('仅可在PAD上使用扫码功能');
          return;
        }
        qrRes = await scanQR(
          shouldScanQRCodeList,
          shouldScanQRCodeList?.[0] === 'verifyCode' ? '扫描服务卡二维码' : undefined
        );

        const formatResult = qrRes?.reduce((accur, cur, index) => {
          accur[shouldScanQRCodeList?.[index]] = cur;
          return accur;
        }, {});

        storeScanCodeRef.current = {
          [selectMachineItem?.deviceId]: {
            ...(storeScanCodeRef.current?.[selectMachineItem?.deviceId] || {}),
            ...(formatResult || {}),
          },
        } as any;
      }

      if (selectPlanItem?.isDraft) {
        lookie.set(CURRENT_ORDER_IS_DRAFT, true);
      } else {
        lookie.remove(CURRENT_ORDER_IS_DRAFT);
      }

      const { data } = await service.service.createServiceOrder({
        customerId: customerId.current,
        storeGoodsId: selectPlanItem?.storeGoodsId,
        deviceId: selectMachineItem.deviceId,
        ...(state?.orderId ? { inheritOrderId: state?.orderId } : {}),
        ...(appCoord || ({} as any)),
        ...(qrRes?.length === 2
          ? {
              consumableUniqueCode: qrRes?.[0],
              verifyCode: qrRes?.[1],
            }
          : qrRes?.length === 1
          ? {
              [shouldScanQRCodeList?.[0]]: qrRes?.[0],
              ...(storedCode?.consumableUniqueCode ? { consumableUniqueCode: storedCode?.consumableUniqueCode } : {}),
              ...(storedCode?.verifyCode ? { verifyCode: storedCode?.verifyCode } : {}),
            }
          : {}),
      });

      const isEnterNextStep = async () => {
        /** 按摩页默认开启沉浸模式 */
        lookie.remove(IS_OPEN_IMMER_MODE);

        const { data: servingOrder } = await service.service.getServingOrderByOperator();
        dispatch(setCurrentServingOrder(servingOrder));
        lookie.set(SELECTED_MACHINE_CAMERA_TYPE, selectMachineItem?.cameraType);
        lookie.remove(HAS_SHOWED_TIPS_MODAL);
        handleNavFromOrderInfo(data, nav);
      };

      if (!!data?.remainingBalance || data?.remainingBalance === 0) {
        Dialog.alert({
          title: '剩余授权使用次数即将不足',
          content: '请及时联系服务商进行充值，避免影响您的使用。',
          confirmText: '我知道了',
          onConfirm() {
            isEnterNextStep();
          },
        });
        return;
      } else {
        isEnterNextStep();
      }
    } catch (e) {
      // pad端软件版本过低的情况
      if (e?.code === UPPER_STATUS_CODE.DEVICE_SOFTWARE_VERSION_TOO_LOW) {
        Dialog.alert({
          title: '机器人升级提醒',
          content: (
            <div>
              <span>软件版本过低，需升级后才可正常运行，</span>
              <br />
              <span>请联系工作人员进行处理</span>
            </div>
          ),
          confirmText: '我知道了',
          onConfirm() {},
        });
      } else if (e.code === UPPER_STATUS_CODE.INSUFFICIENT_AUTHORIZATION_QUOTA) {
        Dialog.alert({
          title: '剩余授权使用次数为0',
          content: '请联系服务商进行充值',
          confirmText: '我知道了',
          onConfirm() {},
        });
      } else if (e?.code === UPPER_STATUS_CODE.OP_DEVICE_SOFTWARE_TOO_LOW) {
        // 机器人软件版本过低的情况
        Dialog.alert({
          title: '平板升级提醒',
          content: (
            <div>
              <span>软件版本过低，需升级后才可正常运行，</span>
              <br />
              <span>请联系工作人员进行处理</span>
            </div>
          ),
          confirmText: '我知道了',
          onConfirm() {},
        });
      } else if (e?.code === UPPER_STATUS_CODE.ChassisLockingOrUnLocking) {
        loopGetDeviceChassisState(selectMachineItem.deviceId);
      } else if (
        e?.code === UPPER_STATUS_CODE.DEVICE_SOFTWARE_BY_PRODUCT_MAX_VERSION ||
        e?.code === UPPER_STATUS_CODE.DEVICE_SOFTWARE_BY_PRODUCT_MIN_VERSION
      ) {
        // 机器人版本不支持某方案
        Dialog.alert({
          title: (
            <div>
              <div>当前机器人版本不支持</div>
              <div style={{ marginTop: '3px' }}>{'「' + selectPlanItem.name + '」方案'}</div>
            </div>
          ),
          content: (
            <div>
              <div>请请联系交付工程师</div>
              <div style={{ marginTop: '3px' }}>
                更新至<span style={{ color: '#0055FF' }}>{e?.message}</span>版本使用
              </div>
            </div>
          ),
          confirmText: '我知道了',
          bodyClassName: cx('software-not-support'),
        });
      } else if (e?.code === UPPER_STATUS_CODE.DEVICE_HAS_BEEN_LOCKED) {
        Dialog.alert({
          title: '设备已锁机',
          content: (
            <div>
              <div>该设备已被代理商远程锁机，</div>
              <div>请联系代理商恢复使用</div>
              {!!e?.message && (
                <div style={{ color: '#0055FF' }}>
                  联系方式：
                  <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{e?.message}</span>
                </div>
              )}
            </div>
          ),
          confirmText: '我知道了',
        });
      } else if (e?.code === UPPER_STATUS_CODE.CONSUMABLE_CODE_INVALID) {
        Toast.show(e?.message, { duration: 5000 });
        if (!!storeScanCodeRef.current?.[selectMachineItem?.deviceId]) {
          storeScanCodeRef.current[selectMachineItem?.deviceId] = {
            consumableUniqueCode: null,
            verifyCode: storeScanCodeRef.current[selectMachineItem?.deviceId]?.verifyCode,
          };
        }
      } else if (e?.code === UPPER_STATUS_CODE.VERIFY_CODE_INVALID) {
        Toast.show(e?.message, { duration: 5000 });
        if (!!storeScanCodeRef.current[selectMachineItem?.deviceId]) {
          storeScanCodeRef.current[selectMachineItem?.deviceId] = {
            consumableUniqueCode: storeScanCodeRef.current[selectMachineItem?.deviceId]?.consumableUniqueCode,
            verifyCode: null,
          };
        }
      } else if (e?.code === UPPER_STATUS_CODE.SELECTED_PLAN_UPDATE) {
        Dialog.alert({
          title: '当前版本方案已更新',
          content: <span>点击下方按钮后将自动刷新成新方案</span>,
          confirmText: '我知道了',
          onConfirm() {
            getAllPlanRef.current?.();
          },
        });
      } else {
        e?.message && Toast.show(e?.message === 'Network Error' ? '网络错误' : e?.message);
      }
    } finally {
      setIsCreatingService(false);
    }
  }, [
    checkIsApp,
    checkLocationPermission,
    dispatch,
    getShouldScanQRCodeList,
    loopGetDeviceChassisState,
    nav,
    scanQR,
    scrollToBottom,
    selectMachineItem,
    selectPlanItem,
    state,
    appCoordInit,
  ]);

  const initAppCoord = async () => {
    const data = await Device.getInfo();

    if (data?.platform !== 'web') {
      const appInfo = await App.getInfo();
      const appCoord = await getLocationCoord(appInfo?.version);
      setAppCoordInit(appCoord);
    }
  };

  const getRemainingQuota = async () => {
    const { data } = await service.service.getStoreRemainingQuota();
    setStoreRemainingInfo(data);
  };

  useEffect(() => {
    customerId.current = lookie.get(SELECTED_CUSTOMER_ID);
    const deviceChassisLoopTimerBackup = deviceChassisLoopTimer.current;
    initAppCoord();
    getRemainingQuota();
    return () => {
      Object.values(deviceChassisLoopTimerBackup || {})?.forEach(timer => clearInterval(timer));
    };
  }, []);

  useEffect(() => {
    storeScanCodeRef.current = {};
  }, [selectMachineItem?.deviceId]);

  return (
    <div className={cx('add-service')}>
      <CustomToast ref={customToastRef} />
      <Header
        title={<HeaderMemberInfo />}
        backConfig={{
          text: '会员详情',
          action: () =>
            nav(ALL_PAGES.MEMBER_DETAIL, {
              state: {
                currentIsExpand: state?.currentIsExpand || state?.from === 'ai-consultation',
              },
            }),
        }}
      />
      <div className={cx('main-content')}>
        <div className={cx('right-box')}>
          <ScrollView ref={scrollViewRef} className={cx('right-box-wrapper')}>
            <div className={cx('wrapper')}>
              <SelectPlan
                getAllPlanRef={getAllPlanRef}
                scrollViewRef={scrollViewRef}
                onChangePlan={planItem => {
                  setSelectPlanItem(planItem);
                }}
                onSelectMachine={setSelectMachineItem}
                setSelectedInfoByUser={setSelectedInfoByUser}
                className={cx('select-plan')}
              />
              {storeRemainingInfo?.enabled === 1 && storeRemainingInfo?.balance !== undefined && (
                <div className={cx('quota-info')}>
                  <Icon name="info-icon-black" className={cx('info-icon')} />
                  <span className={cx('quota-text')}>
                    当前剩余授权使用次数：<span className={cx('quota-num')}>{storeRemainingInfo?.balance}</span>次
                  </span>
                </div>
              )}
            </div>
          </ScrollView>
          <Button
            color="primary"
            loading={isCreatingService}
            disabled={submitBtnIsDisabeld}
            className={cx('btn', 'btn-primary', {
              'show-action-point': !submitBtnIsDisabeld && !isCreatingService,
            })}
            onClick={() => {
              if (shouldShowPopup()) {
                clauseDialogRef.current?.show();
                return;
              }
              handleCreateService();
            }}
          >
            创建服务
          </Button>
        </div>
      </div>
      <DisclaimerDialog ref={clauseDialogRef} />
    </div>
  );
};

export default AddService;
